/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useRef } from 'react';
import { Rating } from '@/components/Rating/Rating';
import styles from './LittlePromoContent.module.scss';
import NextImage from '@/components/NextImage';
import { CertonaAddToCartButton } from '@/features/certona/components/CertonaAddToCartButton';
import { SmartLink as Link } from '@/utils/smartLink';
import { useLabels } from '@/hooks/useLabels';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { CertonaTitle } from '@/features/certona/components/CertonaTitle';

const labelMap = {
  lblTotalReviews: 'label_Carousel_TotalReviews',
  qualifyTitle: 'label_miniCart_littlePromo_qualifyTitle',
  dealTermsHeader: 'label_miniCart_littlePromo_dealTermsHeader',
  dealTermsBody: 'label_miniCart_littlePromo_dealTermsBody',
  addToCartAriaLabel: 'label_miniCart_littlePromo_addToCartAriaLabel',
};

type LittlePromoContentProps = {
  displayPrice: string;
  promotionPrice: string;
  productImageUrl: string;
  productSeoUrl: string;
  productName: string;
  hasReviews: boolean;
  averageRating: number;
  totalReviews: string;
  isLoading: boolean;
  isAdded: boolean;
  handleAddToCart: () => void;
};

export const LittlePromoContent = ({
  displayPrice,
  promotionPrice,
  productImageUrl,
  productSeoUrl,
  productName,
  hasReviews,
  averageRating,
  totalReviews,
  isLoading,
  isAdded,
  handleAddToCart,
}: LittlePromoContentProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { lblTotalReviews, qualifyTitle, dealTermsHeader, dealTermsBody, addToCartAriaLabel } =
    useLabels(labelMap);
  const { setPageSpecificData } = usePageSpecificData();

  const handleProductLinkClick = () => {
    setPageSpecificData({
      productFindingMethod: 'Product Recommendation',
      productRecommendationInteraction: 'productRecommendationInteraction',
      pfmLocation: 'Mini Cart',
      productRecommendationType: 'Mini Cart: Special Offer',
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <CertonaTitle
          title={qualifyTitle}
          tooltipTitle={dealTermsHeader}
          tooltipBody={dealTermsBody}
          tooltipStrategy="absolute"
          tooltipPopperClass={styles.tooltipPopper}
        />
      </div>
      <div className={styles.card}>
        <div
          className={styles.content}
          onClick={() => {
            linkRef.current?.click();
          }}
        >
          <Link
            to={productSeoUrl}
            title={productName}
            data-testid="promo-product-image-link"
            aria-label={productName}
            onClick={handleProductLinkClick}
          >
            <NextImage src={productImageUrl} height={64} width={64} alt="" />
          </Link>

          <div className={styles.productDetails}>
            <div className={styles.reviewsAndPrice}>
              <div className={styles.pricing}>
                <span className={styles.originalPrice}>{displayPrice}</span>
                <span className={styles.promotionPrice}>{promotionPrice}</span>
              </div>
              {hasReviews && (
                <div className={styles.reviews}>
                  <Rating
                    value={averageRating}
                    max={5}
                    showStarFractions
                    isCertonaPartCard={true}
                    ariaLabel={`${productName} rated ${averageRating.toFixed(1)} out of 5 stars`}
                  />
                  <div className={styles.reviewNumber}>
                    <span aria-hidden>{Number(averageRating).toFixed(1)}</span>
                    <span>{` (${totalReviews})`}</span>
                    <span className="sr-only">{lblTotalReviews}</span>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.productInfoAndCTA}>
              <Link
                to={productSeoUrl}
                title={productName}
                data-testid="promo-product-link"
                ref={linkRef}
                aria-label={[
                  displayPrice,
                  promotionPrice,
                  hasReviews && averageRating > 0
                    ? `${averageRating.toFixed(1)} out of 5 stars, ${totalReviews} ${lblTotalReviews}`
                    : null,
                  productName,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={handleProductLinkClick}
              >
                <div className={styles.description}>{productName}</div>
              </Link>
              <div onClick={(e) => e.stopPropagation()}>
                <CertonaAddToCartButton
                  onClick={handleAddToCart}
                  ariaLabel={`${addToCartAriaLabel} ${productName}`}
                  isLoading={isLoading}
                  isAdded={isAdded}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
